import { ExternalLink } from 'lucide-react';
import { Badge, cn, type DataGridConfig } from '@eo/shared';

import type {
  CapacityClientRow,
  CapacityEntryRow,
  ClientReport,
  FinishedTask,
  StaffReport,
} from '../api/types';
import {
  BILLABLE_OPTIONS,
  BUDGET_BUCKETS,
  CLIENT_COLUMN_HELP,
  CLIENT_GRID_PREFIX,
  LOG_GRID_PREFIX,
} from '../constants';
import { formatHours } from '../helpers';
import { LoadBar } from '../LoadBar';
import type { LogFilters, SearchFilterSource, TeamCategoryRow } from '../types';

function TaskWithLink({ row }: { row: CapacityEntryRow }) {
  return (
    <span className="flex max-w-72 items-center gap-2">
      <span className="truncate">{row.taskName}</span>
      {row.taskId && (
        <a
          href={`https://app.clickup.com/t/${row.taskId}`}
          target="_blank"
          rel="noreferrer"
          aria-label={`Open ${row.taskName} in ClickUp`}
          className="shrink-0 text-text-tertiary hover:text-text-primary"
        >
          <ExternalLink className="size-3.5" />
        </a>
      )}
    </span>
  );
}

type GridFilter = NonNullable<DataGridConfig<unknown>['filters']>[number];

function searchFilter(
  id: string,
  header: string,
  allLabel: string,
  source: SearchFilterSource,
): GridFilter {
  return { id, header, filter: { type: 'search', allLabel, ...source } };
}

// Column ids double as the server's sort keys; the person, client and billable
// filters ride the same prefixed URL params the server reads.
export function makeEntriesGridConfig({
  people,
  clients,
}: LogFilters): DataGridConfig<CapacityEntryRow> {
  return {
    getRowId: (row) => row.entryId,
    paramPrefix: LOG_GRID_PREFIX,
    rowTestId: 'entry-row',
    search: { placeholder: 'Search task' },
    filters: [
      ...(people ? [searchFilter('member', 'Person', 'Everyone', people)] : []),
      ...(clients ? [searchFilter('client', 'Client', 'All clients', clients)] : []),
    ],
    emptyState: { title: 'No ClickUp time entries match.' },
    columns: [
      {
        id: 'date',
        header: 'Date',
        sortable: true,
        accessor: (row) => row.startTs,
        cell: (row) => (
          <span className="text-sm text-text-tertiary">
            {new Date(row.startTs).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              timeZone: 'America/New_York',
            })}
          </span>
        ),
      },
      { id: 'member', header: 'Person', sortable: true, accessor: (row) => row.member },
      {
        id: 'client',
        header: 'Client',
        sortable: true,
        accessor: (row) => row.clientName,
        cell: (row) =>
          row.clientName ?? <span className="text-text-tertiary">No client · {row.listName}</span>,
      },
      {
        id: 'task',
        header: 'Task',
        accessor: (row) => row.taskName,
        cell: (row) => <TaskWithLink row={row} />,
      },
      {
        id: 'status',
        header: 'Status',
        accessor: (row) => row.taskStatus,
        cell: (row) => <span className="text-sm text-text-tertiary">{row.taskStatus ?? '—'}</span>,
      },
      {
        id: 'billable',
        header: 'Billing',
        accessor: (row) => row.billable,
        filter: { type: 'select', options: BILLABLE_OPTIONS, allLabel: 'All hours' },
        cell: (row) => (row.billable ? 'Billable' : 'Non-billable'),
      },
      {
        id: 'hours',
        header: 'Hours',
        align: 'right',
        sortable: true,
        accessor: (row) => row.durationHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.durationHours)}</span>,
      },
    ],
  };
}

function NameWithAssignment({ name, assigned }: { name: string; assigned: boolean }) {
  return (
    <span className="flex items-center gap-2">
      {name}
      {!assigned && <Badge variant="outline">Not assigned</Badge>}
    </span>
  );
}

export type ClientMemberRow = ClientReport['assignments'][number];

type ClientMembersGridArgs = { staffName: (userId: string) => string };

export function makeClientMembersGridConfig({
  staffName,
}: ClientMembersGridArgs): DataGridConfig<ClientMemberRow> {
  const varianceCell = (value: number) => (
    <span className={cn('tabular-nums', value > 0 ? 'text-destructive' : 'text-success')}>
      {value > 0 ? '+' : ''}
      {formatHours(value)}
    </span>
  );
  const variance = (row: ClientMemberRow) => row.actualHours - (row.fixedHours + row.variableHours);

  return {
    pagination: false,
    getRowId: (row) => row.userId,
    paramPrefix: 'mem_',
    rowTestId: 'client-member-row',
    emptyState: { title: 'No hour assignments configured for this client.' },
    columns: [
      {
        id: 'member',
        header: 'Member',
        accessor: (row) => staffName(row.userId),
        cell: (row) => <NameWithAssignment name={staffName(row.userId)} assigned={row.assigned} />,
      },
      {
        id: 'fixedHours',
        header: 'Assigned fixed',
        align: 'right',
        accessor: (row) => row.fixedHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.fixedHours)}</span>,
      },
      {
        id: 'variableHours',
        header: 'Assigned variable',
        align: 'right',
        accessor: (row) => row.variableHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.variableHours)}</span>,
      },
      {
        id: 'actualHours',
        header: 'Actual',
        align: 'right',
        accessor: (row) => row.actualHours,
        cell: (row) => <span className="tabular-nums">{formatHours(row.actualHours)}</span>,
      },
      {
        id: 'variance',
        header: 'Variance',
        align: 'right',
        accessor: variance,
        // Nothing was assigned, so there is no plan to vary from.
        cell: (row) => (row.assigned ? varianceCell(variance(row)) : '—'),
      },
    ],
  };
}

export function makeTeamCategoriesGridConfig(): DataGridConfig<TeamCategoryRow> {
  const dash = (value: number | null, render: (v: number) => string) =>
    value === null ? '—' : render(value);

  return {
    getRowId: (row) => row.id,
    pagination: false,
    paramPrefix: 'cat_',
    rowTestId: 'team-category-row',
    emptyState: { title: 'No categories.' },
    columns: [
      { id: 'label', header: 'Category', accessor: (row) => row.label },
      {
        id: 'splitPct',
        header: 'Split',
        align: 'right',
        accessor: (row) => row.splitPct,
        cell: (row) => <span className="tabular-nums">{dash(row.splitPct, (v) => `${v}%`)}</span>,
      },
      {
        id: 'capacityShare',
        header: 'Capacity share',
        align: 'right',
        accessor: (row) => row.capacityShare,
        cell: (row) => <span className="tabular-nums">{dash(row.capacityShare, formatHours)}</span>,
      },
      {
        id: 'actual',
        header: 'Actual',
        align: 'right',
        accessor: (row) => row.actual,
        cell: (row) => <span className="tabular-nums">{formatHours(row.actual)}</span>,
      },
    ],
  };
}

export type TeamClientRow = StaffReport['clientBreakdown'][number];

type TeamClientsGridArgs = { clientName: (id: string) => string };

export function makeTeamClientsGridConfig({
  clientName,
}: TeamClientsGridArgs): DataGridConfig<TeamClientRow> {
  const hours = (value: number) => <span className="tabular-nums">{formatHours(value)}</span>;

  return {
    pagination: false,
    getRowId: (row) => row.clientId,
    paramPrefix: 'tcl_',
    rowTestId: 'team-client-row',
    emptyState: { title: 'No client hours this month.' },
    columns: [
      {
        id: 'client',
        header: 'Client',
        accessor: (row) => clientName(row.clientId),
        cell: (row) => (
          <NameWithAssignment name={clientName(row.clientId)} assigned={row.assigned} />
        ),
      },
      {
        id: 'assignedFixed',
        header: 'Assigned fixed',
        align: 'right',
        accessor: (row) => row.assignedFixed,
        cell: (row) => hours(row.assignedFixed),
      },
      {
        id: 'assignedVariable',
        header: 'Assigned variable',
        align: 'right',
        accessor: (row) => row.assignedVariable,
        cell: (row) => hours(row.assignedVariable),
      },
      {
        id: 'actualFixed',
        header: 'Actual fixed',
        align: 'right',
        accessor: (row) => row.actualFixed,
        cell: (row) => hours(row.actualFixed),
      },
      {
        id: 'actualVariable',
        header: 'Actual variable',
        align: 'right',
        accessor: (row) => row.actualVariable,
        cell: (row) => hours(row.actualVariable),
      },
    ],
  };
}

// Fixed and variable each against their own budget: variable is the billable
// side, so a client on budget in total can still be over where it is billed.
function LoggedByBucket({ row }: { row: CapacityClientRow }) {
  return (
    <div className="flex min-w-48 flex-col gap-2">
      {BUDGET_BUCKETS.map(({ key, label }) => {
        const bucket = row[key];
        return (
          <div key={key} className="flex flex-col gap-1">
            <LoadBar
              label={`${row.name} ${key}: ${formatHours(bucket.actual)} logged of ${formatHours(bucket.budget)} budget`}
              scale={Math.max(bucket.budget, bucket.actual)}
              target={bucket.budget}
              segments={[{ key: 'logged', hours: bucket.actual, className: 'bg-text-tertiary' }]}
            />
            <span className="text-xs tabular-nums text-text-tertiary">
              {label} {formatHours(bucket.actual)} of {formatHours(bucket.budget)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

type ClientCapacityGridArgs = {
  clientHref: (clientId: string) => string;
  clients: SearchFilterSource;
};

// Column ids double as the server's sort keys, so every sortable column here is
// one the route can order by. A row opens that client's detail on the page.
export function makeClientCapacityGridConfig({
  clientHref,
  clients,
}: ClientCapacityGridArgs): DataGridConfig<CapacityClientRow> {
  const hours = (value: number) => <span className="tabular-nums">{formatHours(value)}</span>;

  return {
    getRowId: (row) => row.clientId,
    rowLink: (row) => clientHref(row.clientId),
    paramPrefix: CLIENT_GRID_PREFIX,
    rowTestId: 'client-capacity-row',
    filters: [searchFilter('client', 'Client', 'All clients', clients)],
    emptyState: { title: 'No clients match this filter.' },
    columns: withColumnHelp([
      { id: 'name', header: 'Client', sortable: true, accessor: (row) => row.name },
      { id: 'ownerName', header: 'Owner', accessor: (row) => row.ownerName },
      {
        id: 'budget',
        header: 'Budget',
        align: 'right',
        sortable: true,
        accessor: (row) => row.budget,
        cell: (row) => hours(row.budget),
      },
      {
        id: 'assigned',
        header: 'Assigned',
        align: 'right',
        sortable: true,
        accessor: (row) => row.assigned,
        cell: (row) => (
          <span className="flex flex-col items-end tabular-nums">
            {formatHours(row.assigned)}
            {row.unassigned > 0 && (
              <span className="text-xs text-text-tertiary">
                {formatHours(row.unassigned)} unassigned
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'actual',
        header: 'Logged',
        sortable: true,
        accessor: (row) => row.actual,
        cell: (row) => <LoggedByBucket row={row} />,
      },
      {
        id: 'forecast',
        header: 'Forecast',
        align: 'right',
        sortable: true,
        accessor: (row) => row.forecast,
        cell: (row) => (
          <span className="flex flex-col items-end tabular-nums">
            {formatHours(row.forecast)}
            {row.forecast > row.budget && (
              <span className="text-xs text-destructive">
                {formatHours(row.forecast - row.budget)} over
              </span>
            )}
          </span>
        ),
      },
      {
        id: 'overage',
        header: 'Overage',
        align: 'right',
        sortable: true,
        accessor: (row) => row.overage,
        cell: (row) =>
          row.overage > 0 ? (
            <span className="flex flex-col items-end tabular-nums text-destructive">
              {BUDGET_BUCKETS.filter(({ key }) => row[key].overage > 0).map(({ key, label }) => (
                <span key={key}>
                  {label} {formatHours(row[key].overage)} over
                </span>
              ))}
            </span>
          ) : (
            <span className="text-text-tertiary">—</span>
          ),
      },
    ]),
  };
}

// A finished task's estimate against the time it took: the check Amer reads to
// see how well the team estimates.
export function makeFinishedTasksGridConfig(): DataGridConfig<FinishedTask> {
  const hours = (value: number) => <span className="tabular-nums">{formatHours(value)}</span>;
  return {
    getRowId: (row) => row.taskId,
    paramPrefix: 'fin_',
    rowTestId: 'finished-task-row',
    pagination: false,
    emptyState: { title: 'No estimated tasks finished this month.' },
    columns: [
      { id: 'name', header: 'Finished task', accessor: (row) => row.name },
      {
        id: 'estimateHours',
        header: 'Estimated',
        align: 'right',
        accessor: (row) => row.estimateHours,
        cell: (row) => hours(row.estimateHours),
      },
      {
        id: 'spentHours',
        header: 'Logged',
        align: 'right',
        accessor: (row) => row.spentHours,
        cell: (row) => hours(row.spentHours),
      },
      {
        id: 'difference',
        header: 'Difference',
        align: 'right',
        accessor: (row) => row.spentHours - row.estimateHours,
        cell: (row) => {
          const diff = row.spentHours - row.estimateHours;
          return (
            <span
              className={cn('tabular-nums', diff > 0 ? 'text-destructive' : 'text-text-tertiary')}
            >
              {diff > 0 ? '+' : ''}
              {formatHours(diff)}
            </span>
          );
        },
      },
    ],
  };
}

// Applied by column id, so a column picks up its explanation wherever it sits.
function withColumnHelp<TRow>(
  columns: DataGridConfig<TRow>['columns'],
): DataGridConfig<TRow>['columns'] {
  return columns.map((column) => ({ ...column, info: CLIENT_COLUMN_HELP[column.id] }));
}
